import React, { useEffect, useRef, useState } from 'react';
import {
  signInWithEmailAndPassword, createUserWithEmailAndPassword,
  updateProfile, signInWithCustomToken
} from 'firebase/auth';
import { collection, query, where, getDocs, setDoc, doc, onSnapshot, deleteDoc, addDoc, updateDoc, getDoc, getDocFromServer } from 'firebase/firestore';
import { auth, db } from '../firebase';
import {
  Box, Button, TextField, Typography, Paper, Container, Alert, Tabs, Tab,
  CircularProgress, Divider, Avatar, Stack, Snackbar
} from '@mui/material';
import { WhatsApp, LockOpen, LockOutlined } from '@mui/icons-material';

const financeTheme = {
  bg: 'radial-gradient(1200px 520px at 8% -10%, rgba(59, 130, 246, 0.24), transparent 60%), radial-gradient(980px 460px at 100% 0%, rgba(16, 185, 129, 0.16), transparent 55%), linear-gradient(155deg, #f8fbff 0%, #eaf2ff 50%, #d9e8ff 100%)',
  paper: 'rgba(255, 255, 255, 0.9)',
  paperBorder: 'rgba(37, 99, 235, 0.2)',
  primary: '#1d4ed8',
  primaryHover: '#1e40af',
  text: '#0f172a',
  textSecondary: '#334155',
  divider: 'rgba(100, 116, 139, 0.32)',
  inputBg: '#f8fafc',
};

const darkTextFieldStyle = {
  '& .MuiInputBase-root': {
    color: financeTheme.text,
  },
  '& label': {
    color: financeTheme.textSecondary,
    fontWeight: 600,
  },
  '& label.Mui-focused': {
    color: financeTheme.primary,
  },
  '& .MuiFormLabel-asterisk': {
    color: '#b91c1c',
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: 2.5,
    backgroundColor: financeTheme.inputBg,
    '& input': {
      fontWeight: 500,
    },
    '& input::placeholder': {
      color: '#64748b',
      opacity: 1,
    },
    '& input:-webkit-autofill': {
      WebkitBoxShadow: '0 0 0 100px #f8fafc inset',
      WebkitTextFillColor: '#0f172a',
      caretColor: '#0f172a',
      borderRadius: 10,
    },
    '& fieldset': {
      borderColor: financeTheme.divider,
    },
    '&:hover fieldset': {
      borderColor: financeTheme.textSecondary,
    },
    '&.Mui-focused fieldset': {
      borderColor: financeTheme.primary,
    },
  },
};

const primaryButtonSx = {
  mt: 3,
  mb: 2,
  borderRadius: 2.5,
  py: 1.25,
  fontWeight: 'bold',
  bgcolor: financeTheme.primary,
  '&:hover': { bgcolor: financeTheme.primaryHover }
};

const normalizePhoneBR = (value = '') => {
  let digits = String(value).replace(/\D/g, '');
  if (digits.startsWith('55') && digits.length >= 12) {
    digits = digits.slice(2);
  }
  if (digits.length > 11) {
    digits = digits.slice(-11);
  }
  return digits;
};

const formatPhoneBR = (value = '') => {
  const digits = normalizePhoneBR(value).slice(0, 11);
  if (!digits) return '';
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

const normalizeOtpCode = (value = '') => String(value).replace(/\D/g, '').slice(0, 6);

const buildPhoneDocCandidates = (value = '') => {
  const base = normalizePhoneBR(value);
  const list = new Set();
  if (base) list.add(base);

  if (base && !base.startsWith('55')) list.add(`55${base}`);
  if (base.startsWith('55')) list.add(normalizePhoneBR(base.slice(2)));

  if (base.length === 11 && base[2] === '9') {
    const without9 = `${base.slice(0, 2)}${base.slice(3)}`;
    list.add(without9);
    list.add(`55${without9}`);
  }

  if (base.length === 10) {
    const with9 = `${base.slice(0, 2)}9${base.slice(2)}`;
    list.add(with9);
    list.add(`55${with9}`);
  }

  return [...list].filter(Boolean);
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const sha256Hex = async (value = '') => {
  const normalized = new TextEncoder().encode(String(value));
  const digest = await window.crypto.subtle.digest('SHA-256', normalized);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
};

function Login() {
  const [aba, setAba] = useState(0);
  const [erro, setErro] = useState('');
  const [loading, setLoading] = useState(false);
  const [identificador, setIdentificador] = useState('');
  const [senha, setSenha] = useState('');
  const [nome, setNome] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [avisoZapAberto, setAvisoZapAberto] = useState(false);

  // Login com WhatsApp (passwordless)
  const [modoZap, setModoZap] = useState(false);
  const [telefoneZap, setTelefoneZap] = useState('');
  const [telefoneZapSolicitado, setTelefoneZapSolicitado] = useState('');
  const [codigoDigitado, setCodigoDigitado] = useState('');
  const [etapaZap, setEtapaZap] = useState('pedir_numero'); // pedir_numero -> pedir_codigo
  const [dadosToken, setDadosToken] = useState(null);
  const zapRequestUnsubscribeRef = useRef(null);
  const zapNumeroExibicao = '(62) 98237-3330';
  const zapLink = 'https://wa.me/5562982373330';

  const abrirAvisoZapUmaVez = () => {
    if (sessionStorage.getItem('zapNoticeShown') === '1') return;
    setAvisoZapAberto(true);
    sessionStorage.setItem('zapNoticeShown', '1');
  };

  useEffect(() => {
    return () => {
      if (zapRequestUnsubscribeRef.current) {
        zapRequestUnsubscribeRef.current();
      }
    };
  }, []);

  const registrarAcesso = async (uid, origem) => {
    const now = new Date();
    try {
      await updateDoc(doc(db, 'users', uid), {
        horarioEntrada: now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
        ultimoLoginEm: now.toISOString(),
        atualizadoEm: now.toISOString()
      });

      await addDoc(collection(db, `users/${uid}/logs`), {
        tipo: 'login',
        acao: 'Entrada no sistema',
        origem,
        data: now.toISOString(),
        userAgent: window.navigator.userAgent
      });
    } catch (error) {
      console.warn('Não foi possível registrar acesso:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setErro(''); setLoading(true);
    try {
      let emailParaLogin = identificador;
      if (!identificador.includes('@')) {
        const q = query(collection(db, 'users'), where('username', '==', identificador.toLowerCase()));
        const snap = await getDocs(q);
        if (snap.empty) throw new Error('Usuário não encontrado.');
        emailParaLogin = snap.docs[0].data().email;
      }
      const credencial = await signInWithEmailAndPassword(auth, emailParaLogin, senha);
      await registrarAcesso(credencial.user.uid, 'senha');
      abrirAvisoZapUmaVez();
    } catch (error) {
      setErro('Login ou senha incorretos. Verifique e tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleCadastro = async (e) => {
    e.preventDefault();
    setErro(''); setLoading(true);

    if (username.includes(' ')) { setErro('O username não pode conter espaços.'); setLoading(false); return; }
    const numeroLimpo = normalizePhoneBR(whatsapp);
    if (numeroLimpo.length < 10 || numeroLimpo.length > 11) { setErro('Digite um WhatsApp válido com DDD.'); setLoading(false); return; }

    try {
      const q = query(collection(db, 'users'), where('username', '==', username.toLowerCase()));
      const snap = await getDocs(q);
      if (!snap.empty) throw new Error('Este username já está em uso.');

      const userCredential = await createUserWithEmailAndPassword(auth, email, senha);
      const user = userCredential.user;
      await updateProfile(user, { displayName: nome });

      const dataCriacao = new Date();
      const dataExpiracao = new Date(dataCriacao);
      dataExpiracao.setDate(dataExpiracao.getDate() + 60);

      await setDoc(doc(db, 'users', user.uid), {
        nome: nome,
        username: username.toLowerCase(),
        email: email.toLowerCase(),
        telefone: numeroLimpo,
        telefone_normalizado: numeroLimpo,
        telefone_formatado: formatPhoneBR(numeroLimpo),
        criadoEm: dataCriacao.toISOString(),
        expiraEm: dataExpiracao.toISOString(),
        status: 'trial'
      });
    } catch (error) {
      if (error.message.includes('username')) setErro(error.message);
      else if (error.code === 'auth/email-already-in-use') setErro('Este e-mail já está cadastrado.');
      else if (error.code === 'auth/weak-password') setErro('A senha deve ter pelo menos 6 caracteres.');
      else setErro('Erro ao criar conta. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const solicitarCodigoZap = async () => {
    setErro('');
    setLoading(true);
    setDadosToken(null);
    setCodigoDigitado('');

    const numeroLimpo = normalizePhoneBR(telefoneZap);
    if (numeroLimpo.length < 10) {
      setErro('Digite um número válido com DDD.');
      setLoading(false); return;
    }

    try {
      let snap = await getDocs(query(collection(db, 'users'), where('telefone_normalizado', '==', numeroLimpo)));
      if (snap.empty) {
        snap = await getDocs(query(collection(db, 'users'), where('telefone', '==', numeroLimpo)));
      }
      if (snap.empty) {
        const usersSnap = await getDocs(collection(db, 'users'));
        const encontrado = usersSnap.docs.find((d) => normalizePhoneBR(d.data().telefone || d.data().telefone_normalizado || '') === numeroLimpo);
        if (encontrado) {
          snap = { empty: false, docs: [encontrado] };
        }
      }

      if (snap.empty) {
        setErro('Número não encontrado. Crie uma conta primeiro ou mande um "Oi" no nosso WhatsApp.');
        setLoading(false); return;
      }

      const uid = snap.docs[0].id;
      const loginRequestRef = doc(db, 'login_requests', numeroLimpo);
      setTelefoneZapSolicitado(numeroLimpo);

      await setDoc(loginRequestRef, {
        uid: uid,
        telefone: numeroLimpo,
        status: 'solicitado',
        criadoEm: new Date().toISOString()
      });

      if (zapRequestUnsubscribeRef.current) {
        zapRequestUnsubscribeRef.current();
      }

      zapRequestUnsubscribeRef.current = onSnapshot(loginRequestRef, (docSnap) => {
        if (!docSnap.exists()) return;
        const data = docSnap.data();

        if (data.status === 'pronto') {
          setDadosToken(data);
          return;
        }
        if (data.status === 'erro') {
          setDadosToken(null);
          setErro(data.erro || 'Não foi possível enviar o código. Tente novamente.');
          return;
        }
        if (data.status === 'bloqueado') {
          setDadosToken(data);
          setErro('Muitas tentativas incorretas. Solicite um novo código.');
          return;
        }
        if (data.status === 'expirado') {
          setDadosToken(data);
          setErro('Código expirado. Solicite um novo código.');
        }
      });

      setEtapaZap('pedir_codigo');
    } catch (error) {
      setErro('Erro ao solicitar código. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const confirmarCodigoZap = async () => { // eslint-disable-line no-unused-vars
    setErro(''); setLoading(true);

    if (!dadosToken || !dadosToken.codigo) {
      setErro('Aguarde o recebimento do código no seu WhatsApp.');
      setLoading(false); return;
    }

    if (codigoDigitado === dadosToken.codigo) {
      try {
        const credencial = await signInWithCustomToken(auth, dadosToken.token);
        await registrarAcesso(credencial.user.uid, 'whatsapp');
        await deleteDoc(doc(db, 'login_requests', normalizePhoneBR(telefoneZap)));
        abrirAvisoZapUmaVez();
      } catch (error) {
        setErro('Erro ao autenticar. Tente novamente mais tarde.');
        setLoading(false);
      }
    } else {
      setErro('Código incorreto. Verifique e tente novamente.');
      setLoading(false);
    }
  };

  const confirmarCodigoZapSeguro = async () => {
    setErro('');
    setLoading(true);

    try {
      const codigoLimpo = normalizeOtpCode(codigoDigitado);
      if (codigoLimpo.length !== 6) {
        setErro('Digite os 6 dígitos do código.');
        return;
      }

      const numeroBase = telefoneZapSolicitado || normalizePhoneBR(telefoneZap);
      const docIds = buildPhoneDocCandidates(numeroBase);
      if (!docIds.length) {
        setErro('Informe seu WhatsApp novamente para solicitar um novo código.');
        return;
      }

      const loadRequest = async () => {
        let latest = null;
        for (let tentativa = 0; tentativa < 15; tentativa += 1) {
          for (const docId of docIds) {
            const ref = doc(db, 'login_requests', docId);
            let snap;
            try {
              snap = await getDocFromServer(ref);
            } catch (error) {
              snap = await getDoc(ref);
            }
            if (!snap.exists()) continue;

            const data = snap.data() || {};
            latest = { docId, data };

            const hasCode = Boolean(data.codigoHash || data.codigo);
            if (data.status === 'pronto' && hasCode && data.token) return latest;
            if (['erro', 'bloqueado', 'expirado'].includes(data.status)) return latest;
            if (hasCode && data.token) return latest;
          }

          if (tentativa < 14) await wait(1000);
        }
        return latest;
      };

      const loaded = await loadRequest();
      if (!loaded) {
        setErro('Aguarde o recebimento do código no seu WhatsApp.');
        return;
      }

      const loginRequestRef = doc(db, 'login_requests', loaded.docId);
      const tokenData = loaded.data || {};
      setDadosToken(tokenData);

      if (tokenData.status === 'erro') {
        setErro(tokenData.erro || 'Falha ao enviar código. Solicite novamente.');
        return;
      }
      if (tokenData.status === 'bloqueado') {
        setErro('Código bloqueado por excesso de tentativas. Solicite um novo código.');
        return;
      }
      if (tokenData.status === 'expirado') {
        setErro('Código expirado. Solicite um novo código.');
        return;
      }

      const temCodigo = Boolean(tokenData.codigoHash || tokenData.codigo);
      const temToken = Boolean(tokenData.token);
      if (!temCodigo || !temToken) {
        setErro(`Código ainda em processamento (${tokenData.status || 'sem status'}). Aguarde alguns segundos e tente novamente.`);
        return;
      }

      if (tokenData.expiraEm && new Date(tokenData.expiraEm).getTime() < Date.now()) {
        await updateDoc(loginRequestRef, {
          status: 'expirado',
          atualizadoEm: new Date().toISOString()
        }).catch(() => undefined);
        setErro('Código expirado. Solicite um novo código.');
        return;
      }

      const maxTentativas = Number(tokenData.maxTentativas || 5);
      let codigoValido = false;

      if (tokenData.codigoHash) {
        const codigoHashDigitado = await sha256Hex(codigoLimpo);
        codigoValido = codigoHashDigitado === tokenData.codigoHash;
      } else if (tokenData.codigo) {
        codigoValido = codigoLimpo === String(tokenData.codigo);
      }

      if (codigoValido) {
        const credencial = await signInWithCustomToken(auth, tokenData.token);
        await registrarAcesso(credencial.user.uid, 'whatsapp');
        await deleteDoc(loginRequestRef).catch(() => undefined);
        setTelefoneZapSolicitado('');
        abrirAvisoZapUmaVez();
        return;
      }

      const tentativas = Number(tokenData.tentativas || 0) + 1;
      const restantes = Math.max(maxTentativas - tentativas, 0);

      await updateDoc(loginRequestRef, {
        tentativas,
        status: tentativas >= maxTentativas ? 'bloqueado' : 'pronto',
        atualizadoEm: new Date().toISOString(),
        erro: tentativas >= maxTentativas ? 'Limite de tentativas excedido.' : ''
      }).catch(() => undefined);

      setDadosToken((prev) => ({ ...(prev || {}), tentativas }));
      if (restantes === 0) {
        setErro('Código bloqueado por excesso de tentativas. Solicite um novo código.');
      } else {
        setErro(`Código incorreto. Restam ${restantes} tentativa(s).`);
      }
    } catch (error) {
      setErro(`Não foi possível validar o código agora: ${error?.message || 'erro inesperado'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: financeTheme.bg,
      px: { xs: 1.5, sm: 2 },
      py: { xs: 2, sm: 3 },
      position: 'relative'
    }}>
      <Box sx={{
        position: 'absolute',
        inset: 0,
        background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.12), rgba(148, 163, 184, 0.12) 75%)',
        pointerEvents: 'none'
      }} />

      <Container component="main" maxWidth="xs" sx={{ position: 'relative' }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            backgroundColor: financeTheme.paper,
            color: financeTheme.text,
            borderRadius: 4,
            border: `1px solid ${financeTheme.paperBorder}`,
            boxShadow: '0 24px 52px rgba(37, 99, 235, 0.18)',
            backdropFilter: 'blur(12px)'
          }}
        >
          <Avatar sx={{ m: 1, width: 56, height: 56, bgcolor: 'rgba(37, 99, 235, 0.12)', color: financeTheme.primary, border: '1px solid rgba(37, 99, 235, 0.28)' }}>
            <LockOutlined />
          </Avatar>
          <Typography component="h1" variant="h5" sx={{ color: financeTheme.text, fontWeight: 'bold' }}>
            LNT Tech Finance
          </Typography>
          <Typography variant="body2" sx={{ mb: 2.5, color: financeTheme.textSecondary, textAlign: 'center' }}>
            Sistema de gestão financeira pessoal e empresarial
          </Typography>

          <Tabs
            value={aba}
            onChange={(e, v) => { setAba(v); setErro(''); setModoZap(false); }}
            variant="fullWidth"
            sx={{
              mb: 2,
              width: '100%',
              p: 0.5,
              borderRadius: 99,
              bgcolor: 'rgba(37, 99, 235, 0.1)',
              minHeight: 44,
              '& .MuiTabs-indicator': { display: 'none' },
              '& .MuiTab-root': {
                minHeight: 40,
                borderRadius: 99,
                color: financeTheme.textSecondary,
                textTransform: 'none',
                fontWeight: 600,
              },
              '& .Mui-selected': {
                color: financeTheme.text,
                bgcolor: 'rgba(255, 255, 255, 0.95)',
                border: `1px solid ${financeTheme.paperBorder}`,
              },
            }}
          >
            <Tab label="Entrar" />
            <Tab label="Criar Conta" />
          </Tabs>

          {erro && <Alert severity="error" sx={{ width: '100%', mb: 2, borderRadius: 2 }}>{erro}</Alert>}

          {aba === 0 && !modoZap && (
            <Box component="form" onSubmit={handleLogin} sx={{ width: '100%' }}>
              <Stack spacing={2}>
                <TextField required fullWidth label="E-mail ou Username" value={identificador} onChange={(e) => setIdentificador(e.target.value)} autoFocus sx={darkTextFieldStyle} />
                <TextField required fullWidth label="Senha" type="password" value={senha} onChange={(e) => setSenha(e.target.value)} sx={darkTextFieldStyle} />
              </Stack>
              <Button type="submit" fullWidth variant="contained" disabled={loading} sx={primaryButtonSx}>
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Entrar'}
              </Button>
              <Divider sx={{ my: 2, color: financeTheme.divider, '&::before, &::after': { borderColor: financeTheme.divider } }}>
                <Typography sx={{ color: financeTheme.textSecondary, fontSize: '0.75rem' }}>OU</Typography>
              </Divider>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<WhatsApp />}
                onClick={() => setModoZap(true)}
                sx={{
                  borderRadius: 2.5,
                  py: 1.1,
                  color: '#25D366',
                  borderColor: '#25D366',
                  '&:hover': { borderColor: '#1ebe57', bgcolor: 'rgba(37, 211, 102, 0.1)' }
                }}
              >
                Acessar com WhatsApp
              </Button>
            </Box>
          )}

          {aba === 0 && modoZap && (
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography variant="h6" textAlign="center" mb={2}>Acesso Rápido</Typography>

              {etapaZap === 'pedir_numero' ? (
                <Stack spacing={2} sx={{ width: '100%' }}>
                  <TextField
                    fullWidth
                    label="Seu WhatsApp (com DDD)"
                    placeholder="(62) 99999-9999"
                    helperText="Aceita número com ou sem o 9 adicional."
                    value={telefoneZap}
                    onChange={(e) => setTelefoneZap(formatPhoneBR(e.target.value))}
                    autoFocus
                    sx={darkTextFieldStyle}
                  />
                  <Button variant="contained" onClick={solicitarCodigoZap} disabled={loading} sx={{ borderRadius: 2.5, py: 1.1, bgcolor: '#25D366', '&:hover': { bgcolor: '#1ebe57' } }}>
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Enviar Código'}
                  </Button>
                </Stack>
              ) : (
                <Stack spacing={2} sx={{ width: '100%' }}>
                  {(dadosToken?.codigoHash || dadosToken?.codigo) ? (
                    <Alert severity="success" sx={{ borderRadius: 2 }}>Código enviado no seu WhatsApp.</Alert>
                  ) : (
                    <Alert severity="info" sx={{ borderRadius: 2 }}>Estamos enviando seu código no WhatsApp. Aguarde alguns segundos.</Alert>
                  )}
                  <TextField
                    fullWidth
                    label="Código de 6 dígitos"
                    inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: 20, letterSpacing: 6, color: financeTheme.text } }}
                    value={codigoDigitado}
                    onChange={(e) => setCodigoDigitado(normalizeOtpCode(e.target.value))}
                    autoFocus
                    sx={darkTextFieldStyle}
                  />
                  <Button
                    variant="contained"
                    onClick={confirmarCodigoZapSeguro}
                    disabled={loading}
                    startIcon={!loading && <LockOpen />}
                    sx={{ borderRadius: 2.5, py: 1.1, bgcolor: financeTheme.primary, '&:hover': { bgcolor: financeTheme.primaryHover } }}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Acessar'}
                  </Button>
                </Stack>
              )}

              <Button
                fullWidth
                variant="text"
                sx={{ mt: 2, color: financeTheme.textSecondary }}
                onClick={() => { setModoZap(false); setEtapaZap('pedir_numero'); setErro(''); setDadosToken(null); setTelefoneZapSolicitado(''); }}
              >
                Voltar
              </Button>
            </Box>
          )}

          {aba === 1 && (
            <Box component="form" onSubmit={handleCadastro} sx={{ width: '100%' }}>
              <Stack spacing={2}>
                <TextField required fullWidth label="Nome Completo" value={nome} onChange={(e) => setNome(e.target.value)} sx={darkTextFieldStyle} />
                <TextField required fullWidth label="Username (para login)" placeholder="ex: lucas.tech" value={username} onChange={(e) => setUsername(e.target.value)} sx={darkTextFieldStyle} />
                <TextField required fullWidth label="WhatsApp (com DDD)" placeholder="(62) 99999-9999" value={whatsapp} onChange={(e) => setWhatsapp(formatPhoneBR(e.target.value))} sx={darkTextFieldStyle} />
                <TextField required fullWidth label="E-mail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} sx={darkTextFieldStyle} />
                <TextField required fullWidth label="Senha (mín. 6 caracteres)" type="password" value={senha} onChange={(e) => setSenha(e.target.value)} sx={darkTextFieldStyle} />
              </Stack>
              <Button type="submit" fullWidth variant="contained" disabled={loading} sx={primaryButtonSx}>
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Criar Minha Conta'}
              </Button>
            </Box>
          )}

          <Snackbar
            open={avisoZapAberto}
            autoHideDuration={9000}
            onClose={(_, reason) => {
              if (reason === 'clickaway') return;
              setAvisoZapAberto(false);
            }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert
              severity="info"
              icon={<WhatsApp sx={{ color: '#25D366' }} />}
              action={
                <Button
                  color="success"
                  size="small"
                  variant="contained"
                  startIcon={<WhatsApp />}
                  onClick={() => window.open(zapLink, '_blank', 'noopener,noreferrer')}
                  sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 700 }}
                >
                  Abrir WhatsApp
                </Button>
              }
              sx={{ borderRadius: 2, alignItems: 'center', maxWidth: 420 }}
            >
              Estamos online no WhatsApp {zapNumeroExibicao}. Clique para falar com o suporte LNT Gestão Financeira.
            </Alert>
          </Snackbar>
        </Paper>
      </Container>
    </Box>
  );
}

export default Login;

